/**
 * File: markdown_editor.tsx
 * Description: Controlled component wrapping MDEditor from @uiw/react-md-editor.
 * Allows writing Markdown content with a real-time preview (live mode).
 * Displays an error below the editor if the field is left empty, but only after
 * the user has interacted with it at least once (blur). The visual theme (light/dark)
 * is automatically synced with the app's ThemeModeContext.
 */

import { useState } from 'react';
import MDEditor from '@uiw/react-md-editor';
import Box from '@mui/material/Box';
import FormHelperText from '@mui/material/FormHelperText';
import { useThemeModeContext } from '@/context/theme/theme_mode_context';

/**
 * Props for the MarkdownEditor component.
 *
 * @property {string} value - Current Markdown content of the editor.
 * @property {(content: string) => void} onChange - Callback invoked with the new value whenever the user edits the content.
 */
interface MarkdownEditorProps {
  value: string;
  onChange: (content: string) => void;
}

/**
 * Controlled Markdown editor with empty-field validation.
 *
 * Renders the editor in `live` mode (write and preview panels side by side). Validation
 * is only activated after the first `blur`, preventing errors from showing before the
 * user has had a chance to type. The `data-color-mode` attribute on the wrapper keeps
 * the editor's theme in sync with the app's global color mode.
 *
 * @param {string} props.value - Current Markdown content.
 * @param {(content: string) => void} props.onChange - Callback invoked with the new content on each edit.
 * @returns {JSX.Element}
 */
function MarkdownEditor({ value, onChange }: MarkdownEditorProps) {
  const { mode } = useThemeModeContext();
  const [touched, setTouched] = useState(false);

  const isError = touched && value.trim() === '';

  /**
   * Marks the field as touched on blur, activating real-time validation from that point on.
   */
  function handleBlur() {
    setTouched(true);
  }

  return (
    <Box onBlur={handleBlur} data-color-mode={mode}>
      <MDEditor
        value={value}
        onChange={(val) => {onChange(val ?? '')}}
        preview="live"
        height="35vh"
      />
      {/* visibility:hidden instead of conditional rendering prevents layout shift */}
      <FormHelperText error={isError} sx={{ visibility: isError ? 'visible' : 'hidden' }}>
        El contenido no puede estar vacío
      </FormHelperText>
    </Box>
  );
}

export default MarkdownEditor;
